import { useRef, useState } from "react";
import { Loader2, Plus, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ClearableInput } from "@/components/shared/ClearableInput";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/hooks/use-toast";
import { KEYWORD_FILE_ACCEPT, isAcceptedKeywordFile } from "@/lib/keyword-file";
import { notifySkippedByGlobal } from "@/lib/prohibited-keyword-notices";
import {
  useAddProhibitedKeyword,
  useBulkAddProhibitedKeywords,
} from "@/resources/prohibited-keyword/prohibited-keyword.api";
import { useGetGlobalProhibitedKeywords } from "@/resources/global-prohibited-keyword/global-prohibited-keyword.api";
import type { ProhibitedKeyword } from "@/types/prohibited-keyword";

interface AddProhibitedKeywordsFormProps {
  brandId: number | null;
  existingKeywords: ProhibitedKeyword[];
  singleInputPlaceholder?: string;
}

function parseKeywordList(text: string): string[] {
  return text
    .split(/[,\n]+/)
    .map((k) => k.trim().toLowerCase())
    .filter(Boolean);
}

export function AddProhibitedKeywordsForm({
  brandId,
  existingKeywords,
  singleInputPlaceholder = "e.g. competitor name",
}: AddProhibitedKeywordsFormProps) {
  const [newKeyword, setNewKeyword] = useState("");
  const [bulkText, setBulkText] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addMutation = useAddProhibitedKeyword();
  const bulkAddMutation = useBulkAddProhibitedKeywords();
  // AUT-240: warm the Global list cache so the optimistic add can skip keywords the
  // Global list already blocks -- otherwise they flash into the list and then vanish
  // when the server (which drops them) is reconciled.
  useGetGlobalProhibitedKeywords();

  const notifySearchTermsUpdated = (modifiedEntries: number | undefined, plural: boolean) => {
    if (!modifiedEntries) return;
    toast({
      title: "Search Terms Updated",
      description: `Removed prohibited keyword${plural ? "s" : ""} from ${modifiedEntries} search term entries.`,
    });
  };

  const notifyBulkAdded = (inserted: number, skipped: number) => {
    if (inserted > 0) {
      toast({
        title: "Added",
        description: `${inserted} keyword${inserted !== 1 ? "s" : ""} added${skipped ? `, ${skipped} duplicate${skipped !== 1 ? "s" : ""} skipped` : ""}.`,
      });
    } else {
      toast({ title: "No new keywords", description: "All keywords are already in the list." });
    }
  };

  const handleAddSingle = () => {
    const trimmed = newKeyword.trim().toLowerCase();
    if (!trimmed || !brandId) return;

    if (existingKeywords.some((k) => k.keyword === trimmed)) {
      toast({ title: "Duplicate", description: "This keyword is already in the list." });
      return;
    }

    setNewKeyword("");
    addMutation.mutate(
      { brand_id: brandId, keyword: trimmed, origin: "manual" },
      {
        onSuccess: (result) => {
          toast({ title: "Added", description: `"${trimmed}" added to prohibited keywords.` });
          notifySearchTermsUpdated(result.removed_from_search_terms?.modified_entries, false);
        },
      },
    );
  };

  const submitBulk = (parsed: string[]) => {
    if (!brandId || parsed.length === 0) return;

    bulkAddMutation.mutate(
      { brand_id: brandId, keywords: parsed, origin: "manual" },
      {
        onSuccess: (result) => {
          notifyBulkAdded(result.inserted, result.skipped);
          notifySkippedByGlobal(result.skipped_global, "Brand");
          notifySearchTermsUpdated(result.removed_from_search_terms?.modified_entries, true);
        },
      },
    );
  };

  const handleBulkAdd = () => {
    const parsed = parseKeywordList(bulkText);
    setBulkText("");
    submitBulk(parsed);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!file) return;

    if (!isAcceptedKeywordFile(file)) {
      toast({ title: "Unsupported file type", description: "Please upload a CSV or TXT file." });
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      submitBulk(parseKeywordList(text));
    };
    reader.readAsText(file);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddSingle();
    }
  };

  return (
    <Tabs defaultValue="single" className="w-full">
      <TabsList className="w-full">
        <TabsTrigger value="single" className="flex-1">Add Single</TabsTrigger>
        <TabsTrigger value="bulk" className="flex-1">Bulk Add</TabsTrigger>
        <TabsTrigger value="upload" className="flex-1">File Upload</TabsTrigger>
      </TabsList>

      <TabsContent value="single" className="mt-4">
        <div className="flex gap-2">
          <ClearableInput
            value={newKeyword}
            onChange={setNewKeyword}
            onKeyDown={handleKeyDown}
            placeholder={singleInputPlaceholder}
            wrapperClassName="flex-1"
            icon={null}
          />
          <Button onClick={handleAddSingle} size="sm" disabled={addMutation.isPending}>
            {addMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 mr-1" />
            )}
            Add
          </Button>
        </div>
      </TabsContent>

      <TabsContent value="bulk" className="mt-4 space-y-2">
        <Textarea
          value={bulkText}
          onChange={(e) => setBulkText(e.target.value)}
          placeholder="Paste keywords separated by commas or new lines..."
          rows={5}
        />
        <Button onClick={handleBulkAdd} size="sm" className="w-full" disabled={bulkAddMutation.isPending}>
          {bulkAddMutation.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
          Add All
        </Button>
      </TabsContent>

      <TabsContent value="upload" className="mt-4">
        <input ref={fileInputRef} type="file" accept={KEYWORD_FILE_ACCEPT} onChange={handleFileUpload} className="hidden" />
        <button
          onClick={() => fileInputRef.current?.click()}
          className="w-full flex flex-col items-center gap-2 py-6 rounded-lg border-2 border-dashed border-border hover:border-primary/50 hover:bg-muted/30 transition-colors cursor-pointer"
        >
          <Upload className="h-7 w-7 text-muted-foreground" />
          <div className="text-sm text-muted-foreground">
            Click to upload <span className="font-medium text-foreground">CSV or TXT</span>
          </div>
          <div className="text-xs text-muted-foreground">One keyword per line, or comma-separated</div>
        </button>
      </TabsContent>
    </Tabs>
  );
}
